<template>
  <div class="flex flex-wrap items-center gap-4 rounded border px-4 py-3" :class="status === 'connected' ? 'border-primary-300 bg-secondary-50' : 'border-secondary-200 bg-gray-50'">
    <BaseIcon icon="phone" :class="status === 'connected' ? 'text-primary-500' : 'text-gray-400'" />
    <div class="text-sm text-gray-700">
      <template v-if="status === 'connected'">{{ $t("securone.voice.you_are_in") }}</template>
      <template v-else-if="status === 'connecting'">{{ $t("securone.voice.connecting") }}</template>
      <template v-else-if="status === 'error'"><span class="text-red-600">{{ $t("securone.voice.failed") }}</span> <span class="text-xs text-gray-500">{{ errorMsg }}</span></template>
      <template v-else>{{ $t("securone.voice.channel") }}</template>
      <span v-if="participants.length" class="text-gray-500"> · {{ $t("securone.voice.joined_names", { names: participants.join(", ") }) }}</span>
    </div>
    <div class="ml-auto flex flex-wrap gap-2">
      <template v-if="status === 'connected'">
        <button class="sd-btn sd-btn--secondary" :class="{ 'bg-white text-primary-700 border-primary-300': !muted }" @click="toggleMute">
          <BaseIcon :icon="muted ? 'microphone-slash' : 'microphone'" class="mr-1" />{{ muted ? $t("securone.voice.muted") : $t("securone.voice.mic_on") }}
        </button>
        <button class="sd-btn sd-btn--danger" @click="leave">{{ $t("securone.voice.leave") }}</button>
      </template>
      <button v-else class="sd-btn sd-btn--primary" :disabled="status === 'connecting'" @click="join">
        <BaseIcon icon="phone" class="mr-1" />{{ $t("securone.voice.join") }}
      </button>
    </div>
    <!-- Jitsi iframe kept rendered with real dimensions so remote audio plays; same trick as securapp/voice_room.vue -->
    <div :id="containerId" style="position:absolute;left:-9999px;top:-9999px;width:400px;height:300px;visibility:hidden;pointer-events:none;" aria-hidden="true"></div>
  </div>
</template>

<script>
import api from "../securone.api";

// Voice channel for one broadcast, joined from the desktop the way the alarm
// centre joins a personal alarm's room. The server hands out a token only to
// recipients (sender, alerted recipient, member of a linked group) - this
// component is rendered only when `me.canJoinVoice` is true. Joining is an
// active choice on a category broadcast (automatic joining is for personal
// alarms only).
export default {
  name: "VoiceBox",
  props: {
    eventId: { type: String, required: true },
    // names from the event log's voice_joined rows
    participants: { type: Array, default: () => [] },
    // the event was resolved: the channel ended
    ended: { type: Boolean, default: false },
  },
  data() {
    return {
      status: "idle", // idle | connecting | connected | error
      muted: true,
      errorMsg: "",
      jitsi: null,
      connectTimer: null,
      containerId: "securone-jitsi-" + Math.random().toString(36).slice(2, 8),
    };
  },
  methods: {
    async join() {
      try {
        this.status = "connecting";
        this.errorMsg = "";

        // Same flow as the alarm centre (securtrack/alarm_center.vue): ask for
        // the microphone first so the browser's own permission dialog appears;
        // on denial go back to idle without an error of our own.
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((t) => t.stop());
        } catch (_permError) {
          this.status = "idle";
          return;
        }

        const token = await api.voiceToken(this.eventId);
        if (!token || !token.domain || !token.jwt) throw new Error("Voice token response is incomplete");
        await this.loadScript(token.domain);

        this.jitsi = new window.JitsiMeetExternalAPI(token.domain, {
          roomName: token.roomName,
          jwt: token.jwt,
          parentNode: document.getElementById(this.containerId),
          width: 400,
          height: 300,
          configOverwrite: {
            startAudioOnly: true,
            prejoinConfig: { enabled: false },
            startWithAudioMuted: false,
            disableDeepLinking: true,
            // Jitsi logs every step of the call (XMPP, SDP, peer connections)
            // at trace level into the browser console - keep only errors. The
            // named loggers are the ones Jitsi raises above the default itself.
            logging: {
              defaultLogLevel: "error",
              loggers: {
                "modules/RTC/TraceablePeerConnection.js": "error",
                "modules/sdp/SDPUtil.js": "error",
                "modules/xmpp/ChatRoom.js": "error",
                "modules/xmpp/strophe.util.js": "error",
              },
            },
          },
          interfaceConfigOverwrite: { TOOLBAR_BUTTONS: [], SHOW_JITSI_WATERMARK: false, SHOW_WATERMARK_FOR_GUESTS: false, SHOW_POWERED_BY: false },
        });

        // If the conference never opens (rejected token, prejoin page, network), say so instead of spinning.
        this.connectTimer = setTimeout(() => {
          if (this.status === "connecting") this.failWith("Timed out joining " + token.roomName + " on " + token.domain);
        }, 25000);

        this.jitsi.addEventListener("videoConferenceJoined", () => {
          clearTimeout(this.connectTimer);
          this.status = "connected";
          this.muted = false;
        });
        this.jitsi.addEventListener("videoConferenceLeft", () => this.cleanup());
        this.jitsi.addEventListener("audioMuteStatusChanged", (e) => {
          this.muted = e.muted;
        });
        this.jitsi.addEventListener("errorOccurred", (e) => {
          // Jitsi reports recoverable hiccups here too; only a fatal one ends the attempt.
          const err = e && e.error ? e.error : e;
          if (err && err.isFatal) this.failWith((err.message || err.name || "Jitsi error") + (err.type ? " [" + err.type + "]" : ""));
        });
        this.$emit("joined");
      } catch (error) {
        this.failWith(this.describe(error));
      }
    },
    // The real cause, not a generic line: server errorId / message, HTTP status, or the Jitsi error.
    describe(error) {
      const r = error && error.response;
      if (r) {
        const d = r.data || {};
        if (d.errorId && this.$te("securone.errors." + d.errorId)) return this.$t("securone.errors." + d.errorId);
        return (d.message || d.errorId || "") + " (HTTP " + r.status + ")";
      }
      return (error && error.message) || String(error);
    },
    failWith(message) {
      clearTimeout(this.connectTimer);
      this.status = "error";
      this.errorMsg = message;
      if (this.jitsi) {
        try { this.jitsi.dispose(); } catch (e) { /* already disposed */ }
        this.jitsi = null;
      }
    },
    loadScript(domain) {
      return new Promise((resolve, reject) => {
        if (window.JitsiMeetExternalAPI) return resolve();
        const script = document.createElement("script");
        script.src = `https://${domain}/external_api.js`;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Could not load Jitsi library"));
        document.head.appendChild(script);
      });
    },
    toggleMute() {
      if (this.jitsi) this.jitsi.executeCommand("toggleAudio");
    },
    leave() {
      if (this.jitsi) this.jitsi.executeCommand("hangup");
      this.cleanup();
    },
    cleanup() {
      clearTimeout(this.connectTimer);
      if (this.jitsi) {
        try {
          this.jitsi.dispose();
        } catch (e) {
          /* already disposed */
        }
      }
      this.jitsi = null;
      this.status = "idle";
      this.muted = true;
      this.$emit("left");
    },
  },
  watch: {
    // Resolving in the app stops the voice channel for everyone.
    ended(value) {
      if (value && this.jitsi) this.leave();
    },
  },
  beforeDestroy() {
    if (this.jitsi) this.leave();
  },
};
</script>
